import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, IconButton, Paper, Divider,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableRow, TableContainer,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import Swal from 'sweetalert2';
import { api } from '../../../api';
import MDDatePicker from 'components/MDDatePicker';
import RichTextEditor from 'components/RichTextEditor';


function SemenAnalysisSection({
  semenHistory,
  semenAnalysisOptions,
  maleInvestigationParams,
  spouseInfo,
  canSave,
  canEdit,
  canDelete,
  onSaved,
}) {
  const theme = useTheme();
  const primaryColor = '#1976d2';

  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [editingHistory, setEditingHistory] = useState({});
  const [updatingHistory, setUpdatingHistory] = useState(false);

  useEffect(() => {
    if (!semenHistory) return;
    setHistory(semenHistory.map(h => ({
      id: h.id,
      name: h.param_group_member_option?.value || h.name || '',
      value: h.value || '',
      history_date: h.history_date ? h.history_date.split('T')[0] : '',
      remarks: h.remarks || '',
      irm_param_group_member_options_id:
        h.param_group_member_option_id ?? h.irm_param_group_member_options_id ?? null,
    })));
  }, [semenHistory]);

  const { control, watch, setValue, reset, getValues } = useForm({
    defaultValues: { semenForms: [] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'semenForms' });

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      height: '40px',
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor, borderWidth: 2 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
    '& .MuiSelect-select': { paddingTop: '8px', paddingBottom: '8px' },
  };

  const addForm = useCallback(() => {
    const existingForms = getValues('semenForms');
    const latestDate = existingForms.length > 0 ? existingForms[existingForms.length - 1].date : '';
    append({ test_id: '', test_name: '', date: latestDate, value: '', remarks: '' });
  }, [append, getValues]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return dateStr; }
  };

  // ── Save new entries ──
  const saveSection = useCallback(async () => {
    const forms = getValues('semenForms');
    const valid = forms.filter(f => f.test_id && f.date && f.value);

    if (valid.length === 0) {
      Swal.fire({
        icon: 'warning', title: 'Incomplete',
        text: 'Please fill in Test, Date and Value for at least one entry.',
        timer: 3000, showConfirmButton: false,
      });
      return;
    }

    const semenMember = maleInvestigationParams?.param_group_members?.find(
      m => m.slug === 'male-investigation-params-semen-analysis',
    );

    setSaving(true);
    try {
      const payload = valid.map(f => ({
        contact_id: spouseInfo.id,
        param_group_id: maleInvestigationParams?.id,
        param_group_member_id: semenMember?.id,
        param_group_member_option_id: parseInt(f.test_id, 10),
        name: f.test_name,
        value: f.value,
        history_date: f.date,
        remarks: f.remarks || null,
        private_note: null,
      }));

      await api.opd.createContactHistory(payload);
      Swal.fire({
        icon: 'success', title: 'Saved', text: 'Semen analysis saved successfully.',
        timer: 2500, timerProgressBar: true, showConfirmButton: false,
      });
      reset({ semenForms: [] });
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving semen analysis:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to save. Please try again.' });
    } finally {
      setSaving(false);
    }
  }, [maleInvestigationParams, spouseInfo, getValues, reset, onSaved]);

  // ── Edit handlers ──
  const handleEditHistory = (h) => {
    setEditingHistory(prev => ({
      ...prev,
      [h.id]: {
        name: h.name,
        value: h.value,
        history_date: h.history_date,
        remarks: h.remarks || '',
        irm_param_group_member_options_id: h.irm_param_group_member_options_id || null,
      },
    }));
  };

  const handleCancelEdit = (id) => {
    setEditingHistory(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleSaveEdit = async (h) => {
    const edited = editingHistory[h.id];
    if (!edited) return;
    setUpdatingHistory(true);
    try {
      const updateData = {
        contact_id: spouseInfo.id,
        param_group_id: maleInvestigationParams?.id,
        param_group_member_id: maleInvestigationParams?.param_group_members?.find(
          m => m.slug === 'male-investigation-params-semen-analysis',
        )?.id,
        param_group_member_option_id: edited.irm_param_group_member_options_id
          ? parseInt(edited.irm_param_group_member_options_id, 10)
          : null,
        contact_history_id: h.id,
        value: edited.value,
        history_date: edited.history_date,
        remarks: edited.remarks || null,
      };
      await api.opd.updateContactHistory(updateData);
      setHistory(prev => prev.map(item => item.id === h.id ? { ...item, ...edited } : item));
      handleCancelEdit(h.id);
      Swal.fire({
        icon: 'success', title: 'Updated', text: 'Record updated successfully.',
        timer: 2000, timerProgressBar: true, showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error updating semen analysis:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to update record.' });
    } finally {
      setUpdatingHistory(false);
    }
  };

  const handleDeleteHistory = async (h) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete Record',
      text: `Delete "${h.name}" entry? This action cannot be undone.`,
      showCancelButton: true, confirmButtonColor: '#dc3545', cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await api.opd.deleteContactHistory({ contact_history_ids: [h.id] });
      setHistory(prev => prev.filter(item => item.id !== h.id));
      Swal.fire({
        icon: 'success', title: 'Deleted', text: 'Record deleted.',
        timer: 2000, timerProgressBar: true, showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error deleting semen analysis:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to delete record.' });
    }
  };

  // ── History table ──
  const renderHistoryTable = () => {
    if (!history || history.length === 0) {
      return (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No existing semen analysis history found
          </Typography>
        </Paper>
      );
    }

    const activeEditId = history.find(h => editingHistory[h.id])?.id ?? null;
    const edited = activeEditId ? editingHistory[activeEditId] : null;

    return (
      <Box mb={2}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Test Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Remarks</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px', textAlign: 'center', width: 80 }}>Actions</TableCell>
              </TableRow>
              {history.map(h => {
                const isEditing = !!editingHistory[h.id];
                return (
                  <TableRow key={h.id} hover sx={{ backgroundColor: isEditing ? `${primaryColor}08` : 'inherit' }}>
                    <TableCell sx={{ fontSize: '12px', fontWeight: isEditing ? 600 : 400, color: isEditing ? primaryColor : 'inherit' }}>
                      {h.name || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', maxWidth: 160 }}>
                      <Box dangerouslySetInnerHTML={{ __html: h.value || '-' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(h.history_date)}</TableCell>
                    <TableCell sx={{ fontSize: '12px', maxWidth: 160 }}>
                      <Box dangerouslySetInnerHTML={{ __html: h.remarks || '-' }} />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton size="small" onClick={() => handleSaveEdit(h)} disabled={updatingHistory}
                            sx={{ color: '#28a745', '&:hover': { backgroundColor: 'rgba(40,167,69,0.1)' } }} title="Save">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleCancelEdit(h.id)} disabled={updatingHistory}
                            sx={{ color: '#6c757d', '&:hover': { backgroundColor: 'rgba(108,117,125,0.1)' } }} title="Cancel">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box display="flex" gap={0.5} justifyContent="center">
                          {canEdit !== false && !activeEditId && (
                            <IconButton size="small" onClick={() => handleEditHistory(h)}
                              sx={{ color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}18` } }} title="Edit">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canDelete !== false && !activeEditId && (
                            <IconButton size="small" onClick={() => handleDeleteHistory(h)}
                              sx={{ color: '#dc3545', '&:hover': { backgroundColor: 'rgba(220,53,69,0.1)' } }} title="Delete">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Edit form card */}
        {activeEditId && edited && (
          <Paper elevation={3} sx={{ mt: 1.5, p: 2.5, border: `2px solid ${primaryColor}`, borderRadius: 2, backgroundColor: '#f0f6ff' }}>
            <Typography sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.85rem', mb: 2 }}>
              ✏️ Editing: {history.find(h => h.id === activeEditId)?.name || ''}
            </Typography>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>Test Name</InputLabel>
                  <Select
                    value={edited.irm_param_group_member_options_id ?? ''}
                    label="Test Name"
                    onChange={(e) => {
                      const opt = semenAnalysisOptions.find(o => o.id === e.target.value);
                      setEditingHistory(prev => ({
                        ...prev,
                        [activeEditId]: {
                          ...prev[activeEditId],
                          irm_param_group_member_options_id: e.target.value,
                          name: opt?.option_value || prev[activeEditId].name,
                        },
                      }));
                    }}
                  >
                    {semenAnalysisOptions.map(opt => (
                      <MenuItem key={opt.id} value={opt.id}>{opt.option_value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  label="Date"
                  value={edited.history_date || ''}
                  onChange={(date) =>
                    setEditingHistory(prev => ({
                      ...prev,
                      [activeEditId]: { ...prev[activeEditId], history_date: date },
                    }))
                  }
                  textFieldProps={{ fullWidth: true, size: 'small', sx: inputSx }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}>
                <RichTextEditor
                  label="Value"
                  value={edited.value || ''}
                  onChange={(val) =>
                    setEditingHistory(prev => ({
                      ...prev,
                      [activeEditId]: { ...prev[activeEditId], value: val },
                    }))
                  }
                  height="120px"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RichTextEditor
                  label="Remarks"
                  value={edited.remarks || ''}
                  onChange={(val) =>
                    setEditingHistory(prev => ({
                      ...prev,
                      [activeEditId]: { ...prev[activeEditId], remarks: val },
                    }))
                  }
                  height="120px"
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" gap={1}>
              <button
                type="button"
                onClick={() => handleCancelEdit(activeEditId)}
                disabled={updatingHistory}
                style={{
                  padding: '8px 20px', background: 'transparent', color: '#6c757d',
                  border: '1px solid #6c757d', borderRadius: 4, cursor: 'pointer',
                  fontSize: '0.875rem', fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSaveEdit(history.find(h => h.id === activeEditId))}
                disabled={updatingHistory}
                style={{
                  padding: '8px 20px',
                  background: updatingHistory ? '#ccc' : '#28a745',
                  color: updatingHistory ? '#999' : '#fff',
                  border: 'none', borderRadius: 4,
                  cursor: updatingHistory ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem', fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <SaveIcon style={{ fontSize: '1rem' }} />
                {updatingHistory ? 'Saving...' : 'Save Changes'}
              </button>
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ mb: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden', border: `1px solid ${primaryColor}30` }}>
      {/* Section header */}
      <Box
        sx={{
          px: 2, py: 1.5,
          borderBottom: `2px solid ${primaryColor}`,
          backgroundColor: `${primaryColor}0d`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <Typography sx={{ color: primaryColor, fontWeight: 'bold', fontSize: '1rem' }}>
          Semen Analysis
        </Typography>
        {canSave !== false && (
          <button
            type="button"
            onClick={addForm}
            disabled={saving}
            style={{
              padding: '6px 14px', background: 'transparent',
              color: primaryColor, border: `1px solid ${primaryColor}`,
              borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem', fontWeight: '500',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <AddIcon style={{ fontSize: '1rem' }} />
            Add Test
          </button>
        )}
      </Box>

      <Box sx={{ p: 2 }}>
        {renderHistoryTable()}

        <Divider sx={{ my: 2 }} />

        {fields.length === 0 ? (
          <Paper sx={{ p: 2.5, backgroundColor: '#f8f9fa', textAlign: 'center', borderRadius: 1, border: '1px dashed #dee2e6' }}>
            <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              No semen analysis tests added. Click &quot;Add Test&quot; above to get started.
            </Typography>
          </Paper>
        ) : (
          <>
            {fields.map((field, index) => (
              <Paper key={field.id} sx={{ p: 2, mb: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography sx={{ color: primaryColor, fontWeight: 'bold', fontSize: '0.85rem' }}>
                    Test #{index + 1}
                  </Typography>
                  <IconButton onClick={() => remove(index)} size="small"
                    sx={{ color: '#dc3545', '&:hover': { backgroundColor: 'rgba(220,53,69,0.1)' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  {/* Test Name */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`semenForms.${index}.test_id`}
                      control={control}
                      render={({ field: f }) => (
                        <FormControl fullWidth size="small" sx={inputSx}>
                          <InputLabel>Test Name *</InputLabel>
                          <Select
                            {...f}
                            label="Test Name *"
                            onChange={(e) => {
                              f.onChange(e);
                              const opt = semenAnalysisOptions.find(o => String(o.id) === String(e.target.value));
                              if (opt) setValue(`semenForms.${index}.test_name`, opt.option_value || '');
                            }}
                          >
                            {semenAnalysisOptions.length === 0 ? (
                              <MenuItem disabled value="">No options available</MenuItem>
                            ) : (
                              semenAnalysisOptions.map(opt => (
                                <MenuItem key={opt.id} value={String(opt.id)}>{opt.option_value}</MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {/* Date */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`semenForms.${index}.date`}
                      control={control}
                      render={({ field: f }) => (
                        <MDDatePicker
                          label="Date *"
                          value={f.value || ''}
                          onChange={async (date) => {
                            f.onChange(date);
                            const allForms = getValues('semenForms');
                            const blanks = allForms.filter((form, i) => i !== index && !form.date);
                            if (blanks.length > 0) {
                              const res = await Swal.fire({
                                title: 'Apply Date to All?',
                                text: `Apply this date to ${blanks.length} empty date field(s)?`,
                                icon: 'question', showCancelButton: true,
                                confirmButtonColor: primaryColor, cancelButtonColor: '#6c757d',
                                confirmButtonText: 'Yes, apply to all', cancelButtonText: 'No',
                              });
                              if (res.isConfirmed) {
                                allForms.forEach((form, i) => {
                                  if (i !== index && !form.date) setValue(`semenForms.${i}.date`, date);
                                });
                              }
                            }
                          }}
                          textFieldProps={{ fullWidth: true, size: 'small', required: true, sx: inputSx }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Value */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`semenForms.${index}.value`}
                      control={control}
                      render={({ field: f }) => (
                        <RichTextEditor
                          label="Value *"
                          value={f.value || ''}
                          onChange={f.onChange}
                          height="120px"
                        />
                      )}
                    />
                  </Grid>

                  {/* Remarks */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`semenForms.${index}.remarks`}
                      control={control}
                      render={({ field: f }) => (
                        <RichTextEditor
                          label="Remarks"
                          value={f.value || ''}
                          onChange={f.onChange}
                          height="120px"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {canSave !== false && (
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <button
                  type="button"
                  onClick={saveSection}
                  disabled={saving}
                  style={{
                    padding: '10px 28px',
                    background: saving ? '#ccc' : primaryColor,
                    color: saving ? '#999' : '#fff',
                    border: 'none', borderRadius: 4,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '1rem', fontWeight: '500',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'background 0.3s ease',
                  }}
                  onMouseOver={(e) => { if (!saving) e.currentTarget.style.background = '#1565c0'; }}
                  onMouseOut={(e) => { if (!saving) e.currentTarget.style.background = primaryColor; }}
                >
                  <SaveIcon style={{ fontSize: '1.2rem' }} />
                  {saving ? 'Saving...' : 'Save Semen Analysis'}
                </button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}

SemenAnalysisSection.propTypes = {
  semenHistory: PropTypes.arrayOf(PropTypes.object),
  semenAnalysisOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      option_value: PropTypes.string,
    }),
  ),
  maleInvestigationParams: PropTypes.object,
  spouseInfo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  canSave: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onSaved: PropTypes.func,
};

SemenAnalysisSection.defaultProps = {
  semenHistory: [],
  semenAnalysisOptions: [],
  maleInvestigationParams: null,
  spouseInfo: null,
  canSave: true,
  canEdit: true,
  canDelete: true,
  onSaved: null,
};

export default SemenAnalysisSection;
